<template>
    <div>
        <div class="w-full flex flex-col">
            <div class="modal-header flex items-center justify-between cursor-move">
                <h1 class="flex-1 text-2xl">Manage Units</h1>
                <div @click="hideManageUnitsModal">
                    <inline-svg name="close" classes="w-5 h-5 text-white fill-current cursor-pointer"></inline-svg>
                </div>
            </div>
            <div class="modal-toolbar h-20 flex items-center justify-between">
                <div class="flex flex-col">
                    <p class="font-semibold uppercase text-xl">{{ buildings.selectedBuilding.address_line_1 }}</p>
                    <p class="uppercase text-sm">{{ buildings.selectedBuilding.city }}, {{ buildings.selectedBuilding.state }}</p>
                </div>
                <div>
                    <button
                        @click.prevent="createNewUnit"
                        class="ml-2 flex items-center btn btn-small btn-indigo">
                            <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                            <span class="ml-2">New Unit</span>
                    </button>
                </div>
            </div>
            <div class="flex w-full">
                <div v-if="! units.selectedUnitId" class="p-4 w-full" style="min-height: 200px">
                    <table v-if="buildings.selectedBuilding.units" class="mt-4 w-full">
                        <thead class="cursor-pointer">
                            <tr>
                                <th class="text-center">Floor</th>
                                <th class="text-center">Suite</th>
                                <th class="text-right">Size</th>
                                <th class="text-right">Divisible</th>
                                <th class="text-right">Status</th>
                                <th class="text-right">TI Allowance</th>
                                <th class="text-right">Last Update</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="unit in buildings.selectedBuilding.units" :key="unit.id">
                                <td class="text-center">{{ unit.floor }}</td>
                                <td class="text-center">{{ unit.suite }}</td>
                                <td class="text-right">{{ unit.size | number }}</td>
                                <td class="text-right">{{ unit.divisible | number }}</td>
                                <td class="text-right">{{ unit.status }}</td>
                                <td class="text-right">{{ formatCurrency(unit.ti_allowance, 2) }}</td>
                                <td class="text-right">{{ unit.updated_at | date }}</td>
                                <td class="text-right">
                                    <div class="flex justify-end space-x-3">
                                        <button @click="units.selectedUnitId = unit.id" type="button" class="btn-action">Edit</button>
                                        <button @click="confirmDeleteUnit(unit.id)" type="button" class="ml-3 btn-action">Delete</button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="mt-8 flex flex-col h-full items-center justify-center space-y-2">
                        <span class="text-xl">No Units Found</span>
                        <button
                            type="button"
                            @click.prevent="createNewUnit"
                            class="flex items-center btn btn-small btn-indigo">
                                <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                                <span class="ml-2">Create Unit</span>
                        </button>
                    </div>
                </div>
                <div v-else class="w-full">
                    <div class="modal-small-toolbar">
                        <div class="pl-2 flex items-center space-x-2">
                            <a @click="showUnitList">Units</a>
                            <div v-if="units.selectedUnitId != null" class="flex items-center space-x-2">
                                <span>/</span>
                                <span v-if="units.selectedUnitId == 'new'">New Unit</span>
                                <span v-else>Edit Unit</span>
                            </div>
                        </div>
                    </div>
                    <div class="p-4">
                        <div class="flex items-center">
                            <nav class="-mb-px flex space-x-2" aria-label="Sections">
                                <button
                                    v-for="(section, slug) in enabledSections" :key="slug"
                                    @click="currentSection = slug"
                                    class="py-2 px-3 border border-gray-200 flex whitespace-nowrap rounded-t-lg text-sm"
                                    :class="{ 'bg-gray-50 text-gray-500 font-medium hover:border-gray-200 hover:text-gray-700': slug != currentSection, 'bg-gradient-to-br from-blue-500 to-blue-700 font-bold text-white': slug == currentSection }">
                                        {{ section.label }}
                                    <span
                                        v-if="section.showCount"
                                        class="py-0.5 px-2.5 ml-3 hidden rounded-full text-xs font-medium md:inline-block"
                                        :class="{ 'bg-gray-200 text-gray-900': slug != currentSection, 'bg-blue-800 text-white': slug == currentSection }">
                                            {{ section.count }}
                                    </span>
                                </button>
                            </nav>
                        </div>
                        <div v-if="currentSection == 'unit_info'">
                            <form class="p-6 border bg-gray-50">
                                <div class="px-2 py-3 flex flex-wrap gap-y-6 gap-x-8">
                                    <div class="flex flex-col">
                                        <label class="text-sm font-semibold" for="floor">Floor</label>
                                        <select name="floor" v-model="selectedUnit.floor" class="mt-0 form-select max-w-fit">
                                            <option value=""></option>
                                            <option v-for="floor in floors" :key="floor" :value="floor">{{ floor }}</option>
                                        </select>
                                        <div v-if="errors.floor" class="form-error">{{ errors.floor[0] }}</div>
                                    </div>

                                    <div class="flex flex-col">
                                        <label class="text-sm font-semibold" for="suite">Suite</label>
                                        <input type="text" class="form-input" name="suite" size="6" v-model="selectedUnit.suite">
                                        <div v-if="errors.suite" class="form-error">{{ errors.suite[0] }}</div>
                                    </div>

                                    <div class="flex flex-col">
                                        <label class="text-sm font-semibold" for="description">Status</label>
                                        <select class="mt-0 form-select flex-none max-w-fit" name="status" v-model="selectedUnit.status">
                                            <option value=""></option>
                                            <option value="Available">Available</option>
                                            <option value="Occupied">Occupied</option>
                                            <option value="Sublease">Sublease</option>
                                            <option value="Vacant">Vacant</option>
                                        </select>
                                        <div v-if="errors.status" class="form-error">{{ errors.status[0] }}</div>
                                    </div>

                                    <div class="flex flex-col">
                                        <label class="text-sm font-semibold" for="size">Size</label>
                                        <number-input name="size" v-model="selectedUnit.size" classes="text-sm text-right"></number-input>
                                        <div v-if="errors.size" class="form-error">{{ errors.size[0] }}</div>
                                    </div>

                                    <div class="flex flex-col">
                                        <label class="text-sm font-semibold" for="divisble">Divisible</label>
                                        <number-input name="divisible" v-model="selectedUnit.divisible" classes="text-sm text-right"></number-input>
                                        <div v-if="errors.divisible" class="form-error">{{ errors.divisible[0] }}</div>
                                    </div>

                                    <div class="flex flex-col">
                                        <label class="text-sm font-semibold">Renewal Space?</label>
                                        <select class="mt-0 form-select flex-none max-w-fit" name="is_renewal_space" v-model="selectedUnit.is_renewal_space">
                                            <option value=""></option>
                                            <option value="0">No</option>
                                            <option value="1">Yes</option>
                                        </select>
                                        <div v-if="errors.is_renewal_space" class="form-error">{{ errors.is_renewal_space[0] }}</div>
                                    </div>

                                    <div class="flex flex-col">
                                        <label class="text-sm font-semibold" for="ti_allowance">TI Allowance</label>
                                        <currency-input name="ti_allowance" v-model="ti_allowance" is-decimal></currency-input>
                                        <div v-if="errors.ti_allowance" class="form-error">{{ errors.ti_allowance[0] }}</div>
                                    </div>

                                    <div class="w-full flex flex-col">
                                        <label class="text-sm font-semibold" for="description">Comments</label>
                                        <textarea class="w-full form-textarea" name="comment" rows="6" v-model="selectedUnit.comment" :v-text="selectedUnit.comment"></textarea>
                                        <div v-if="errors.comment" class="form-error">{{ errors.comment[0] }}</div>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div v-if="currentSection == 'expenses'" class="p-5 w-full border bg-gray-50">
                            <unit-expenses />
                        </div>
                    </div>
                </div>
            </div>
            <div class="bg-gray-100 p-4">
                <div class="flex items-center justify-end">
                    <button class="btn btn-secondary mr-2" @click.prevent="hideManageUnitsModal">Close</button>
                    <div v-if="units.selectedUnitId">
                        <button v-if="currentSection == 'unit_info' && (aUnitIsSelected || units.selectedUnitId == 'new')" class="btn btn-green" @click.prevent="saveUnit">
                            <span v-if="loading">
                                <inline-svg name="loading-button"></inline-svg>
                            </span>
                            <span v-if="! loading">Save Unit</span>
                        </button>
                        <button v-if="currentSection == 'expenses'" class="btn btn-green" @click.prevent="saveExpense">
                            <span v-if="loading">
                                <inline-svg name="loading-button"></inline-svg>
                            </span>
                            <span v-if="! loading">Save Expense</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <notifications classes="notification" position="bottom right"></notifications>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';

    export default {
        props: {
            selectedSection: {
                type: String,
                default: 'unit_info'
            }
        },

        data() {
            return {
                currentSection: this.selectedSection,
                selectedExpenseId: '',
                ti_allowance: '',
                errors: '',
                loading: false
            }
        },

        created() {
            this.expenses.all = this.selectedUnit.expenses;
        },

        mounted() {
            this.ti_allowance = this.ti;

            this.$eventHub.$on('change-section', (section) => {
                this.currentSection = section;
            });
        },

        watch: {
            // selectedExpenseId() {
            //     if (this.selectedExpenseId != 'new') {
            //         this.$emit('expenseSaved', this.selectedExpenseId);
            //     }
            // }

            selectedUnit() {
                if (this.selectedUnitId == null || this.selectedUnitId == 'new') {
                    this.ti_allowance = '';
                } else {
                    this.expenses.all = this.selectedUnit.expenses;
                    this.ti_allowance = this.ti;
                }
            }
        },

        computed: {
            enabledSections() {
                // v-for used to be paired with v-if="section.enabled"; Vue 2 runs
                // v-for first so the list was already filtered. Same result, and
                // it does not break when the directive priority reverses in Vue 3.
                const source = this.sections;
                return Array.isArray(source)
                    ? source.filter(entry => entry.enabled)
                    : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
            },

            ...mapState(['buildings']),
            ...mapState(['units']),
            ...mapState(['expenses']),
            ...mapGetters(['selectedUnit']),
            ...mapGetters(['selectedExpense']),

            sections() {
                return {
                    unit_info: {
                        label: "Unit",
                        enabled: true
                    },
                    expenses: {
                        label: "Expenses",
                        enabled: this.units.selectedUnitId != 'new',
                        showCount: true,
                        count: this.expenses.all.length || 0
                    }
                };
            },

            aUnitIsSelected() {
                return this.units.selectedUnitId != 'new' && this.units.selectedUnitId != ''
            },

            ti: {
                get() {
                    return this.selectedUnit ? this.formatCurrency(this.selectedUnit.ti_allowance, 2) : 0;
                },

                set(newValue) {
                    this.ti_allowance = newValue;
                }
            },

            floors() {
                return this.buildings.selectedBuilding.floors || 1;
            }
        },

        methods: {
            hideManageUnitsModal() {
                if (this.selectedUnitId == 'new') {
                    this.removeUnit('new');
                }

                this.$modal.hide('manage-units');
            },

            showUnitList() {
                this.removeUnit('new');
                this.units.selectedUnitId = null
            },

            createNewUnit() {
                this.units.all.push({
                    id: 'new',
                    building_id: this.buildings.selectedBuilding.id,
                    expenses: [],
                    status: null,
                    is_renewal_space: null,
                    size: null,
                    divisible: null,
                    floor: this.buildings.selectedBuilding.floors ? null : 1,
                    suite: null,
                    ti_allowance: null,
                    comment: null
                });

                this.expenses.mode = null;
                this.expenses.selectedExpenseId = null;
                this.expenses.all = [];
                this.units.selectedUnitId = 'new';
                this.currentSection = 'unit_info';
            },

            createUnit() {
                this.loading = true;

                axios.post('/units', this.selectedUnit)
                    .then((response) => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        });

                        this.buildings.selectedBuilding.units = _.remove(this.buildings.selectedBuilding.units, (unit) => {
                            return unit.id != 'new';
                        });

                        this.units.all = _.remove(this.units.all, (unit) => {
                            return unit.id != 'new';
                        });

                        this.buildings.selectedBuilding.units.push(response.data.unit);
                        this.expenses.all = [];
                        this.units.all.push(response.data.unit);
                        this.units.filtered.push(response.data.unit);
                        this.units.selectedUnitId = response.data.unit.id;

                    })
                    .catch((error) => {
                        console.log(error);
                        this.errors = error.response.data.errors;
                    }).finally(() => {
                        this.loading = false;
                    });
            },

            updateUnit() {
                this.loading = true;

                axios.patch('/units/' + this.selectedUnit.id, this.selectedUnit)
                    .then((response) => {
                        this.selectedUnit.ti_allowance = this.formatCurrency(this.selectedUnit.ti_allowance, 2);

                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        });
                    })
                    .catch((error) => {
                        console.log(error);
                        this.errors = error.response.data.errors;
                    }).finally(() => {
                        this.loading = false;
                    });
            },

            saveUnit() {
                this.errors = [];

                this.selectedUnit.ti_allowance = this.prepCurrency(this.ti_allowance);

                if (this.units.selectedUnitId == "new") {
                    this.createUnit();
                } else {
                    this.updateUnit();
                }
            },

            async saveExpense() {
                this.errors = [];

                await this.$store.dispatch('save').then(() => {
                    this.expenses.mode = null;
                    this.expenses.selectedExpenseId = null;

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: "Expense Saved"
                    });
                })
            },

            confirmDeleteUnit(unitId) {
                this.$swal({
                    title: 'Delete this unit?',
                    text: "ALL data will be lost, including any lease information attached to this unit",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteUnit(unitId);
                    }
                });
            },

            deleteUnit(unitId) {
                if (unitId == 'new') {
                    this.removeUnit(unitId);

                    return;
                }

                axios.delete('/units/' + unitId).then((response) => {
                    this.removeUnit(unitId);

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });
                }).catch((error) => {
                    console.log(error);
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
                });
            },

            removeUnit(unitId) {
                if (unitId == 'new') {
                    this.units.all = _.remove(this.units.all, (unit) => { return unit.id != unitId });

                    return;
                }

                var unit = _.find(this.buildings.selectedBuilding.units, ['id', unitId]);

                if (! unit) {
                    return;
                }

                var stackSize = this.buildings.selectedBuilding.stack[unit.floor][unit.status];

                // remove the unit
                this.buildings.selectedBuilding.units = _.remove(this.buildings.selectedBuilding.units, (unit) => { return unit.id != unitId });
                this.units.all = _.remove(this.units.all, (unit) => { return unit.id != unitId });

                // update the stack chart
                this.buildings.selectedBuilding.stack[unit.floor][unit.status] = stackSize - unit.size;
            },
        }
    }
</script>
